import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { trackPageview } from "./api";
import Header from "./components/Header";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import ConciergeWidget from "./components/ConciergeWidget";
import ActionHub from "./components/ActionHub";
import AiDock from "./components/AiDock";
import { Toaster } from "./components/ui/toaster";
import Home from "./pages/Home";
import Buying from "./pages/Buying";
import Lettings from "./pages/Lettings";
import About from "./pages/About";
import Services from "./pages/Services";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import PropertyDetail from "./pages/PropertyDetail";
import Favourites from "./pages/Favourites";
import Calculators from "./pages/Calculators";
import LinkInBio from "./pages/LinkInBio";
import BioPage from "./pages/BioPage";
import DevelopmentsPage from "./pages/DevelopmentsPage";
import DevelopmentDetail from "./pages/DevelopmentDetail";
import AppPage from "./pages/AppPage";
import LoginPage from "./pages/LoginPage";
import Portal from "./pages/Portal";
import Admin from "./pages/Admin";
import Marketing from "./pages/Marketing";
import Growth from "./pages/Growth";
import Legal from "./pages/Legal";
import CookieConsent from "./components/CookieConsent";
import AreaGuides from "./pages/AreaGuides";
import AreaGuideDetail from "./pages/AreaGuideDetail";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // First-party pageview tracking for public pages only (excludes team workspaces)
    const isWorkspace = ["/admin", "/marketing", "/growth", "/portal", "/login"].some((p) => pathname.startsWith(p));
    if (!isWorkspace) {
      let sid = localStorage.getItem("anadear_sid");
      if (!sid) { sid = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("anadear_sid", sid); }
      trackPageview({ path: pathname, referrer: document.referrer || "", sessionId: sid });
    }
    if (hash) {
      const id = hash.slice(1);
      let tries = 0;
      const timer = setInterval(() => {
        const el = document.getElementById(id);
        if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); clearInterval(timer); }
        else if (++tries > 40) clearInterval(timer);
      }, 100);
      return () => clearInterval(timer);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function FloatingTools() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const isWorkspace = ["/admin", "/marketing", "/growth"].some((p) => pathname.startsWith(p));
  const isTeam = user && (user.role === "admin" || user.role === "agent");
  return (
    <>
      {!isWorkspace && <WhatsAppButton />}
      {!isWorkspace && <ConciergeWidget />}
      {!isWorkspace && <ActionHub />}
      {isWorkspace && isTeam && <AiDock />}
    </>
  );
}

function Shell() {
  const { pathname } = useLocation();
  const bare = ["/bio", "/link"].some((p) => pathname.startsWith(p));
  return (
    <>
      <ScrollToTop />
      {!bare && <Header />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/buying" element={<Buying />} />
          <Route path="/lettings" element={<Lettings />} />
          <Route path="/about-us" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/area-guides" element={<AreaGuides />} />
          <Route path="/area-guides/:slug" element={<AreaGuideDetail />} />
          <Route path="/property/:slug" element={<PropertyDetail />} />
          <Route path="/favourites" element={<Favourites />} />
          <Route path="/calculators" element={<Calculators />} />
          <Route path="/developments" element={<DevelopmentsPage />} />
          <Route path="/developments/:id" element={<DevelopmentDetail />} />
          <Route path="/app" element={<AppPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signin" element={<LoginPage />} />
          <Route path="/link" element={<LinkInBio />} />
          <Route path="/bio" element={<BioPage />} />
          <Route path="/portal" element={<Portal />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/marketing" element={<Marketing />} />
          <Route path="/growth" element={<Growth />} />
          <Route path="/privacy-policy" element={<Legal />} />
          <Route path="/terms-and-conditions" element={<Legal />} />
          <Route path="/cookie-policy" element={<Legal />} />
          <Route path="/complaints-procedure" element={<Legal />} />
        </Routes>
      </main>
      {!bare && <Footer />}
      {!bare && <FloatingTools />}
      {!bare && <CookieConsent />}
      <Toaster />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
